/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Message } from 'primereact/message';
import { ProgressBar } from 'sharedUi/ProgressBar';
import { Button } from 'sharedUi/Button';
import { Input } from 'sharedUi/Input';
import { SectionLabel, StatusChip } from '../common/panel';
import { evalCasesService } from '../../services/eval-cases.service';
import { evalSuitesService } from '../../services/eval-suites.service';
import { extractErrorMessage } from '../../utils/error-handler';
import { parseRows, safeCode, toDrafts, type CaseDraft, type ParsedDataset } from '../../utils/eval-dataset';
import type { EvalSuite } from '../../types';

/**
 * Import a dataset into a suite: every row becomes an eval case, the cases join
 * the suite, and the suite is ready to run.
 *
 * The suite already knows WHAT to run against and HOW to score, so the file only
 * has to supply the questions and expectations. Each case takes the suite's type,
 * which is also what decides which columns are kept — a RAG suite keeps expected
 * sources, an agent suite keeps expected tools, a prompt suite keeps neither —
 * matching what the case form shows for each.
 *
 * Cases are created first, a few at a time, and joined to the suite in ONE update
 * at the end: hundreds of concurrent "add one case" calls would each rewrite the
 * suite's case list and could overwrite one another.
 */

const CONCURRENCY = 4;
const PREVIEW_ROWS = 5;

interface Props {
  suite: EvalSuite;
  visible: boolean;
  onHide: () => void;
  /** Called with the number of cases that joined the suite. */
  onImported: (added: number) => void;
  /** "Run it now": hand over to the suite's own Run dialog, which picks the model. */
  onRunRequested: () => void;
}

type Phase = 'pick' | 'preview' | 'importing' | 'done';

const today = () => new Date().toISOString().slice(0, 10).replace(/-/g, '');

const SAMPLE = 'question,expected_answer,expected_sources\n'
  + '"Under the POCSO Act, 2012, who is a ""child""?",below the age of eighteen years,protection_of_children_from_sexual_offences_pocso_act_2012.pdf';

const DatasetImportDialog = ({ suite, visible, onHide, onImported, onRunRequested }: Props) => {
  const fileInput = useRef<HTMLInputElement>(null);
  const [phase, setPhase] = useState<Phase>('pick');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [prefix, setPrefix] = useState('');
  const [parseError, setParseError] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  const [failures, setFailures] = useState<{ row: number; caseCode: string; reason: string }[]>([]);
  const [added, setAdded] = useState(0);
  const [joinError, setJoinError] = useState<string | null>(null);

  const suiteType = String(suite.type || '').toLowerCase();
  const defaultPrefix = safeCode(`${suite.suiteCode || 'suite'}_ds_${today()}`);
  const parsed: ParsedDataset | null = useMemo(
    () => (rows.length ? toDrafts(rows, prefix || defaultPrefix) : null),
    [rows, prefix, defaultPrefix]);

  const reset = () => {
    setPhase('pick'); setFileName(''); setRows([]); setPrefix(''); setParseError(null);
    setDone(0); setFailures([]); setAdded(0); setJoinError(null);
  };
  const close = () => { if (phase !== 'importing') { reset(); onHide(); } };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setParseError(null);
    try {
      const parsedRows = parseRows(file.name, await file.text());
      if (!parsedRows.length) throw new Error('The file has a header but no rows.');
      setFileName(file.name); setRows(parsedRows); setPhase('preview');
    } catch (e: any) {
      setParseError(e?.message || String(e));
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  /** Only the fields this suite's type is graded on — the same rule as the form. */
  const payloadFor = (d: CaseDraft) => ({
    caseCode: d.caseCode,
    name: d.name,
    type: [suiteType],
    caseType: suiteType === 'agent' ? 'AGENT_TASK' : 'QUESTION_ANSWER',
    input: d.input,
    expectedResult: d.expectedResult,
    requiredFacts: suiteType === 'agent' ? [] : d.requiredFacts,
    context: suiteType === 'agent' ? [] : d.context,
    expectedSources: suiteType === 'rag' ? d.expectedSources : [],
    expectedToolCalls: suiteType === 'agent' && d.expectedToolNames.length
      ? d.expectedToolNames.map((name) => ({ name, arguments: null, order: null }))
      : null,
    tags: [...new Set([...d.tags, 'DATASET'])],
    appId: (suite as any).appId || undefined,
  });

  const runImport = async () => {
    if (!parsed?.drafts.length || !suite._id) return;
    setPhase('importing'); setDone(0); setFailures([]); setJoinError(null);
    const created: string[] = [];
    const failed: typeof failures = [];
    const queue = [...parsed.drafts];

    const worker = async () => {
      for (let d = queue.shift(); d; d = queue.shift()) {
        try {
          const c = await evalCasesService.create(payloadFor(d) as any);
          if (c?._id) created.push(c._id);
        } catch (e) {
          failed.push({ row: d.row, caseCode: d.caseCode, reason: extractErrorMessage(e, 'could not be created') });
        } finally {
          setDone((n) => n + 1);
        }
      }
    };
    await Promise.all(Array.from({ length: CONCURRENCY }, worker));
    setFailures(failed);

    if (created.length) {
      try {
        // Read the suite fresh: its case list may have changed since the page loaded.
        const current = await evalSuitesService.getById(suite._id);
        const existing = [...(current.cases || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const have = new Set(existing.map((c) => String(c.caseRef)));
        const joined = [
          ...existing.map((c, i) => ({ caseRef: String(c.caseRef), order: i, modelIdOverride: c.modelIdOverride ?? null })),
          ...created.filter((id) => !have.has(id)).map((id, i) => ({ caseRef: id, order: existing.length + i, modelIdOverride: null })),
        ];
        await evalSuitesService.update(suite._id, { cases: joined });
        setAdded(joined.length - existing.length);
        onImported(joined.length - existing.length);
      } catch (e) {
        // The cases exist; only the join failed. Say so, so nobody re-imports and
        // collides with the cases this run already created.
        setJoinError(`${created.length} case(s) were created but could not be added to the suite: `
          + `${extractErrorMessage(e, 'the suite update failed')}. Add them from the suite's Edit screen.`);
      }
    }
    setPhase('done');
  };

  const total = parsed?.drafts.length || 0;
  const pct = total ? Math.round((done / total) * 100) : 0;
  const mappingRows = parsed ? Object.entries(parsed.mapping) as [keyof CaseDraft, string][] : [];
  const needs = suiteType === 'rag' ? 'expectedSources' : suiteType === 'agent' ? 'expectedToolNames' : null;

  return (
    <Dialog
      header={`Import a dataset into ${suite.name}`}
      visible={visible} onHide={close} modal
      style={{ width: '52rem', maxWidth: '95vw' }}
      closable={phase !== 'importing'}
      data-testid="DatasetImport.Dialog"
    >
      <input ref={fileInput} type="file" accept=".csv,.json,.jsonl,.ndjson" className="hidden"
             onChange={(e) => onFile(e.target.files?.[0])} data-testid="DatasetImport.File" />

      {phase === 'pick' && (
        <div className="flex flex-col gap-4">
          <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            One row per case. Each row becomes a <span className="font-medium">{suiteType || 'suite'}</span> case in this
            suite, scored by its metrics, so the file only needs the question and what a good answer contains.
          </p>
          <div className="ui-inset p-3 text-xs leading-relaxed text-ink-muted">
            <div className="mb-1 font-medium text-gray-800 dark:text-gray-200">Columns it recognises</div>
            <div><span className="font-mono">question</span> / <span className="font-mono">input</span> — required</div>
            <div><span className="font-mono">expected_answer</span> / <span className="font-mono">expected</span></div>
            {suiteType === 'rag' && <div><span className="font-mono">expected_sources</span> — document file names, separated by <span className="font-mono">|</span></div>}
            {suiteType !== 'agent' && <div><span className="font-mono">context</span> (ground truth), <span className="font-mono">required_facts</span></div>}
            {suiteType === 'agent' && <div><span className="font-mono">expected_tools</span> — tool names, separated by <span className="font-mono">|</span></div>}
            <div><span className="font-mono">case_code</span>, <span className="font-mono">name</span>, <span className="font-mono">tags</span> — optional</div>
            <pre className="mt-2 overflow-x-auto whitespace-pre font-mono text-[11px]">{SAMPLE}</pre>
          </div>
          {parseError && <Message severity="error" text={parseError} className="w-full" />}
          <div className="flex justify-end">
            <span className="ui-press inline-flex">
              <Button type="button" label="Choose a CSV, JSON or JSONL file" icon="pi pi-upload"
                      onClick={() => fileInput.current?.click()} data-testid="DatasetImport.Choose" />
            </span>
          </div>
        </div>
      )}

      {phase === 'preview' && parsed && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="font-mono text-xs text-ink-muted">{fileName}</span>
            <StatusChip tone="success" dot={false}>{total} case{total === 1 ? '' : 's'} ready</StatusChip>
            {parsed.skipped.length > 0 && <StatusChip tone="warn" dot={false}>{parsed.skipped.length} skipped</StatusChip>}
          </div>

          <div>
            <SectionLabel icon="pi pi-sitemap">Columns read</SectionLabel>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {mappingRows.map(([field, col]) => (
                <span key={field} className="ui-tone ui-tone--neutral">
                  <span className="font-mono">{col}</span><span aria-hidden>→</span>{field}
                </span>
              ))}
            </div>
            {needs && !parsed.mapping[needs] && (
              <p className="mt-1.5 text-xs text-amber-700 dark:text-amber-400">
                No {needs === 'expectedSources' ? 'expected-sources' : 'expected-tools'} column: the
                {needs === 'expectedSources' ? ' retrieval metrics' : ' TOOL_CALLS metric'} will have nothing to compare against.
              </p>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="text-ink-muted">
                <tr><th className="py-1 pr-3 font-medium">Row</th><th className="py-1 pr-3 font-medium">Case code</th>
                  <th className="py-1 pr-3 font-medium">Question</th><th className="py-1 font-medium">Expected</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                {parsed.drafts.slice(0, PREVIEW_ROWS).map((d) => (
                  <tr key={d.caseCode} className="align-top">
                    <td className="py-1.5 pr-3 tabular-nums text-gray-500">{d.row}</td>
                    <td className="py-1.5 pr-3 font-mono">{d.caseCode}</td>
                    <td className="max-w-[18rem] py-1.5 pr-3 text-gray-800 dark:text-gray-200">{d.input}</td>
                    <td className="max-w-[14rem] py-1.5 text-ink-muted">{d.expectedResult || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > PREVIEW_ROWS && (
              <p className="mt-1 text-xs text-ink-muted">…and {total - PREVIEW_ROWS} more</p>
            )}
          </div>

          {parsed.skipped.length > 0 && (
            <div className="text-xs text-amber-700 dark:text-amber-400">
              {parsed.skipped.slice(0, 5).map((s) => <div key={s.row}>Row {s.row}: {s.reason}</div>)}
              {parsed.skipped.length > 5 && <div>…and {parsed.skipped.length - 5} more</div>}
            </div>
          )}

          <Input label="Case code prefix (for rows without a case code)" value={prefix}
                 onChange={(v: string) => setPrefix(safeCode(v))} placeholder={defaultPrefix} />

          <div className="flex justify-end gap-2">
            <span className="ui-press inline-flex"><Button type="button" label="Choose another file" severity="secondary" text onClick={reset} /></span>
            <span className="ui-press inline-flex">
              <Button type="button" label={`Import ${total} case${total === 1 ? '' : 's'}`} icon="pi pi-check"
                      disabled={!total} onClick={runImport} data-testid="DatasetImport.Import" />
            </span>
          </div>
        </div>
      )}

      {phase === 'importing' && (
        <div className="flex flex-col gap-2" data-testid="DatasetImport.Progress">
          <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300">
            <span className="tabular-nums">Creating cases… {done} / {total}</span>
            <span className="tabular-nums font-semibold">{pct}%</span>
          </div>
          <ProgressBar value={pct} showValue={false} style={{ height: '6px' }} />
        </div>
      )}

      {phase === 'done' && (
        <div className="flex flex-col gap-3" data-testid="DatasetImport.Done">
          {added > 0 && (
            <Message severity="success" className="w-full"
                     text={`${added} case${added === 1 ? '' : 's'} added to ${suite.name}.`} />
          )}
          {joinError && <Message severity="error" text={joinError} className="w-full" />}
          {failures.length > 0 && (
            <div>
              <SectionLabel icon="pi pi-exclamation-triangle">{failures.length} row{failures.length === 1 ? '' : 's'} not imported</SectionLabel>
              <div className="mt-1.5 max-h-48 overflow-auto text-xs text-gray-700 dark:text-gray-300">
                {failures.map((f) => (
                  <div key={f.caseCode} className="py-0.5">
                    Row {f.row} <span className="font-mono">{f.caseCode}</span> — {f.reason}
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <span className="ui-press inline-flex">
              <Button type="button" label="Close" severity="secondary" outlined onClick={close} data-testid="DatasetImport.Close" />
            </span>
            {added > 0 && (
              // The suite's Run dialog, not a blind trigger: a prompt suite needs a
              // model chosen, and the registry's default has no API key.
              <span className="ui-press inline-flex">
                <Button type="button" label="Run the suite now" icon="pi pi-play" severity="success"
                        onClick={() => { reset(); onHide(); onRunRequested(); }} data-testid="DatasetImport.Run" />
              </span>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default DatasetImportDialog;
